import { useQuery } from "@tanstack/react-query";

import { getDeal, type Deal } from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { DealDetailContent } from "@/components/deals/DealDetailContent";
import type { PartnerQuickAction } from "@/components/deals/dealDetailStatus";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

export interface DealDetailDrawerProps {
  /** Open when a deal id is set; null closes the drawer. */
  dealId: string | null;
  onClose: () => void;
  /** Grid Quick Action that opened the drawer — auto-opens the matching
   * existing confirm dialog once the deal loads (no new mutation paths). */
  initialAction?: PartnerQuickAction | null;
  /**
   * CHP-808: render the master record WITHOUT the owner-only write actions
   * (Log deal progress / Request Closed-Won / Mark Closed-Lost). Set true when
   * the drawer is opened from a manager/PO insights explorer, where the viewer
   * is never the deal owner — POST /deals/{id}/notes and the win/loss endpoints
   * are owner/creator-only and 404 a non-owner, so showing those affordances is
   * a guaranteed dead-end. The partner's own deals list leaves this false (the
   * BDM/PO IS the owner there). Defaults false so the owner path is unchanged.
   */
  readOnly?: boolean;
}

/**
 * Slide-out deal detail at 65% viewport width (full-width on mobile). Fetches
 * the deal on open and renders the shared DealDetailContent — same body as the
 * standalone /deals/$dealId route, which is preserved for direct URLs. The
 * primary partner UX is this drawer, opened from the workspace grid.
 */
export function DealDetailDrawer({
  dealId,
  onClose,
  initialAction,
  readOnly = false,
}: DealDetailDrawerProps) {
  const { accessToken } = useCurrentUser();

  const deal = useQuery<Deal>({
    queryKey: ["deals", "detail", dealId, accessToken],
    queryFn: () => getDeal(accessToken!, dealId!),
    enabled: !!accessToken && !!dealId,
  });

  const d = deal.data;

  return (
    <Sheet
      open={dealId != null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:w-[65vw] sm:max-w-[65vw]"
        data-testid="deal-detail-drawer"
      >
        <SheetHeader>
          {/* [Client Name] / [Deal ID]; the status pill lives in the Status
              section below (CHP-234, strict trim). */}
          <SheetTitle className="pr-8">{d?.client_name ?? "Deal detail"}</SheetTitle>
          <SheetDescription>
            {deal.isLoading ? "Loading deal…" : (d?.deal_id_display ?? "Registered deal details")}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4">
          {deal.isLoading ? (
            <p role="status">Loading…</p>
          ) : deal.error ? (
            <p role="alert">Couldn't load this deal.</p>
          ) : d ? (
            <DealDetailContent
              deal={d}
              onActionDone={onClose}
              initialAction={initialAction}
              readOnly={readOnly}
            />
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
